import React, {useLayoutEffect, useRef} from 'react';

import type {
  ParameterStructuredTable,
  ParameterStructuredTableRowMetadata,
} from 'notebook/integrations/procedures/ParameterStructuredTableAdapter';
import ActionFunctionButton from 'notebook/structured-tables/action-functions/ActionFunctionButton';
import makeCustomButtonActionFunction from 'notebook/structured-tables/action-functions/makeCustomButtonActionFunction';
import unsupportedOverflowMenuItem from 'notebook/structured-tables/action-functions/unsupportedOverflowMenuItem';
import {ActionFunctionParameters} from 'notebook/structured-tables/StructuredTableConstants';
import {ZINDEX_MODAL} from 'styles/StyleConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import runModal from 'ui-library/runModal';

import {
  getParameterConfirmationPivotSpec,
  hasFormulaCells,
  PIVOT_FORMULA_TOOLTIP,
} from './ParameterConfirmationTranspose';
import PivotModal from './PivotModal';
import {LiveTable, LiveTableRegistration, LiveTableRegistry} from './reconcile/LiveTable';

type PivotActionFunctionParameters = ActionFunctionParameters<{}, ParameterStructuredTableRowMetadata>;

export const PIVOT_LOCKED_TOOLTIP = 'This table is locked.';

/** The tables with a mounted Pivot button, which the modal reads and writes at Save. */
const pivotLiveRegistry = new LiveTableRegistry();

async function openPivotModal(params: PivotActionFunctionParameters): Promise<void> {
  const structuredTable = params.structuredTable as ParameterStructuredTable;
  const pivotSpec = getParameterConfirmationPivotSpec(structuredTable);
  if (pivotSpec == null) {
    return;
  }
  await runModal(
    PivotModal,
    {
      structuredTable,
      pivotSpec,
      validationErrors:
        params.note?.noteRuntimeSnapshot?.validationErrorSnapshot?.[structuredTable.name] ?? [],
      documentId: params.entryOrTemplateId,
      liveRegistry: pivotLiveRegistry,
    },
    {
      // Sized like the legacy `extraLarge` modal, at the theme's full height.
      size: '98%',
      variant: 'full-height',
      centered: true,
      // Flex columns, so the grid fills the height below the modal's header.
      styles: {
        content: {display: 'flex', flexDirection: 'column'},
        body: {display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0},
      },
      // The default `pop` transition leaves `transform: scale(1)` on the content, which makes the
      // `position: fixed` cell editor position against the modal instead of the viewport.
      transitionProps: {transition: 'fade'},
      // `PivotModal` handles Escape itself so it can confirm discarding unsaved changes.
      closeOnEscape: false,
      withCloseButton: false,
      zIndex: ZINDEX_MODAL,
    }
  );
}

/**
 * Opens the pivot modal, and registers the notebook's current table for the modal to read at Save.
 */
function PivotButton({params}: {params: PivotActionFunctionParameters}): JSX.Element {
  const {structuredTable, entryOrTemplateId, locked, updateHandler, storeState} = params;
  const registrationRef = useRef<LiveTableRegistration | null>(null);

  // A layout effect, so the registry holds the rendered table before the user can click Save. A passive
  // effect can run a frame later, and a Save in between would merge into the previous table.
  useLayoutEffect(() => {
    const live: LiveTable = {
      structuredTable,
      locked,
      write: (next) => {
        // One notebook Undo reverts the whole Save.
        storeState?.();
        updateHandler({updatedCells: next.rows.map((row) => row.cells), columnUpdates: {}});
      },
    };
    if (registrationRef.current == null) {
      registrationRef.current = pivotLiveRegistry.register(
        entryOrTemplateId,
        structuredTable.provenanceUuid,
        live
      );
    } else {
      registrationRef.current.update(live);
    }
  }, [structuredTable, entryOrTemplateId, locked, updateHandler, storeState]);

  useLayoutEffect(
    () => () => {
      registrationRef.current?.unregister();
      registrationRef.current = null;
    },
    []
  );

  const hasFormulas = hasFormulaCells(structuredTable as ParameterStructuredTable);
  const disabledTooltip = locked ? PIVOT_LOCKED_TOOLTIP : hasFormulas ? PIVOT_FORMULA_TOOLTIP : null;
  return (
    <ActionFunctionButton
      tooltip={disabledTooltip ?? ''}
      buttonStyle={ButtonStyle.TERTIARY_GRAY}
      disabled={disabledTooltip != null}
      icon="angle-90"
      buttonText="Pivot"
      onClick={() => {
        openPivotModal(params);
      }}
    />
  );
}

/**
 * Toolbar action that opens a parameter-confirmation table's pivot grid in `PivotModal`. Hidden when
 * the table can't pivot. Rendered as a text-only toolbar button; it only lives in the `primary` slot,
 * so it is never asked to render as an overflow menu item.
 */
export const pivotActionFunction = makeCustomButtonActionFunction<{}, ParameterStructuredTableRowMetadata>({
  getHidden: (params) =>
    getParameterConfirmationPivotSpec(params.structuredTable as ParameterStructuredTable) == null,
  renderToolbarButton: (params) => <PivotButton params={params} />,
  renderOverflowMenuItem: unsupportedOverflowMenuItem,
});
